// src/pages/spending/SpendingPrepaidsList.tsx
import { useTranslation } from "react-i18next";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiGet } from "../../api/client";
import EmptyState from "../../components/EmptyState";
import {
  ListToolbar,
  MenuItem,
  SortOption,
} from "../../components/ListToolbar";
import { companyRoute } from "../../utils/routeHelpers";
import { formatAnyDateToLocale } from "../../utils/dateUtils";
import { formatMoney } from "../../utils/formatNumber";
import { A, pillClass, PillTone } from "../../styles/appleClasses";
import { Button } from "../../components/Button";

type PrepaidItem = {
  id: number;
  date: string; // YYYY-MM-DD (or datetime string)
  vendor_name?: string | null;
  bank_name?: string | null;
  bank_account_id?: number | null;
  memo?: string | null;
  original_amount: number;
  remaining_amount: number;
  status?: string | null; // open/closed/voided (or similar)
};

function downloadCsv(
  filename: string,
  headers: string[],
  rows: (string | number)[][],
) {
  const esc = (v: any) => {
    const s = String(v ?? "");
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };

  const csv =
    [headers.map(esc).join(","), ...rows.map((r) => r.map(esc).join(","))].join(
      "\n",
    ) + "\n";

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

type DisplayStatus = "open" | "partial" | "closed" | "voided";

const statusLabel = (status: DisplayStatus) => {
  switch (status) {
    case "voided":
      return "Voided";
    case "closed":
      return "Closed";
    case "partial":
      return "Partial";
    case "open":
    default:
      return "Open";
  }
};

const statusTone = (status: DisplayStatus): PillTone => {
  switch (status) {
    case "closed":
      return "success";
    case "partial":
      return "warning";
    case "voided":
      return "neutral";
    case "open":
    default:
      return "brand";
  }
};

// Keep YYYY-MM-DD for date range filtering + sorting (string compares are safe for YMD)
const shortDate = (d?: string | null) => (d || "").slice(0, 10);

function formatDate(input?: string | null) {
  return formatAnyDateToLocale(input);
}

const displayStatusFor = (p: PrepaidItem): DisplayStatus => {
  const s = (p.status || "").toLowerCase().trim();
  if (s === "voided") return "voided";

  const orig = Number(p.original_amount || 0);
  const rem = Number(p.remaining_amount || 0);

  if (rem <= 0 || s === "closed" || s === "done") return "closed";
  if (orig > 0 && rem > 0 && rem < orig) return "partial";
  return "open";
};

const ROWS_PER_PAGE = 10;

export default function SpendingPrepaidsList() {
  const { t } = useTranslation();
  // Canonical statusLabel remains English for search and CSV.
  const displayStatusLabel = (status: DisplayStatus) => {
    switch (status) {
      case "voided":
        return t("sales.list.voided");
      case "closed":
        return t("spendingDeposits.statusClosed");
      case "partial":
        return t("spendingDeposits.statusPartial");
      case "open":
      default:
        return t("spendingDeposits.statusOpen");
    }
  };
  const { company_id = "" } = useParams<{ company_id: string }>();
  const navigate = useNavigate();
  const go = (path: string) => navigate(companyRoute(company_id, path));
  const openPrepaid = (id: number) => go(`/prepaids/${id}`);

  const getC = async <T,>(path: string): Promise<T> => {
    const sep = path.includes("?") ? "&" : "?";
    return apiGet<T>(`${path}${sep}company_id=${company_id}`);
  };

  const collator = useMemo(
    () => new Intl.Collator(undefined, { numeric: true, sensitivity: "base" }),
    [],
  );

  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [sortValue, setSortValue] = useState("date_desc");

  const [items, setItems] = useState<PrepaidItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<{ message: string } | { fallback: true } | null>(null);

  const [page, setPage] = useState(1);

  const sortOptions: SortOption[] = [
    { label: t("sales.list.dateNewest"), value: "date_desc" },
    { label: t("sales.list.dateOldest"), value: "date_asc" },
    { label: t("directory.vendors.sortAsc"), value: "vendor_asc" },
    { label: t("directory.vendors.sortDesc"), value: "vendor_desc" },
    { label: t("spendingDeposits.remainingHigh"), value: "remaining_desc" },
    { label: t("spendingDeposits.remainingLow"), value: "remaining_asc" },
  ];

  const newItems: MenuItem[] = [
    { label: t("spendingPrepaids.new"), onClick: () => go("/prepaids/new") },
  ];

  useEffect(() => {
    if (!company_id) return;
    setLoading(true);
    setErr(null);

    getC<PrepaidItem[]>("/prepaids")
      .then((d) => setItems(d || []))
      .catch((e: any) =>
        setErr(e?.message ? { message: e.message } : { fallback: true }),
      )
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company_id]);

  const filtered = useMemo<PrepaidItem[]>(() => {
    const q = (search || "").toLowerCase().trim();
    const from = fromDate;
    const to = toDate;

    const list = [...(items || [])].filter((it) => {
      const d = shortDate(it.date); // YYYY-MM-DD
      if (from && d < from) return false;
      if (to && d > to) return false;

      if (!q) return true;

      const ds = displayStatusFor(it);
      const dsLabel = statusLabel(ds).toLowerCase();
      const disp = formatDate(it.date).toLowerCase(); // MM/DD/YYYY

      return (
        (it.vendor_name || "").toLowerCase().includes(q) ||
        (it.status || "").toLowerCase().includes(q) ||
        dsLabel.includes(q) ||
        String(it.original_amount ?? "").includes(q) ||
        String(it.remaining_amount ?? "").includes(q) ||
        d.includes(q) || // allow searching by YYYY-MM-DD
        disp.includes(q) // allow searching by MM/DD/YYYY
      );
    });

    const cmpText = (a: string, b: string) =>
      collator.compare(a || "", b || "");
    const cmpNum = (a: number, b: number) => (a || 0) - (b || 0);

    list.sort((a, b) => {
      switch (sortValue) {
        case "date_asc":
          return shortDate(a.date).localeCompare(shortDate(b.date));
        case "vendor_asc":
          return cmpText(a.vendor_name || "", b.vendor_name || "");
        case "vendor_desc":
          return -cmpText(a.vendor_name || "", b.vendor_name || "");
        case "remaining_asc":
          return cmpNum(a.remaining_amount, b.remaining_amount);
        case "remaining_desc":
          return -cmpNum(a.remaining_amount, b.remaining_amount);
        case "date_desc":
        default:
          return shortDate(b.date).localeCompare(shortDate(a.date));
      }
    });

    return list;
  }, [items, search, fromDate, toDate, sortValue, collator]);

  // reset paging on filter/sort/data changes
  useEffect(() => {
    setPage(1);
  }, [search, fromDate, toDate, sortValue, items.length]);

  const totalFiltered = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / ROWS_PER_PAGE));
  const fromRow = totalFiltered === 0 ? 0 : (page - 1) * ROWS_PER_PAGE + 1;
  const toRow = Math.min(page * ROWS_PER_PAGE, totalFiltered);

  // clamp page if data shrinks
  useEffect(() => {
    setPage((p) => Math.min(Math.max(1, p), totalPages));
  }, [totalPages]);

  const paged = useMemo(
    () => filtered.slice((page - 1) * ROWS_PER_PAGE, page * ROWS_PER_PAGE),
    [filtered, page],
  );

  const moreItems: MenuItem[] = [
    {
      label: t("directory.common.exportCsv"),
      onClick: () => {
        const headers = [
          "Date",
          "Vendor",
          "Original",
          "Remaining",
          "Status",
          "ID",
        ];
        const rows = filtered.map((p) => {
          const ds = displayStatusFor(p);
          return [
            formatDate(p.date), // export in MM/DD/YYYY
            p.vendor_name || "",
            p.original_amount ?? "",
            p.remaining_amount ?? "",
            statusLabel(ds),
            p.id,
          ];
        });
        downloadCsv("prepaids.csv", headers, rows);
      },
    },
  ];

  const money = (n: number) => formatMoney(Number(n || 0), "$");

  const pagerText = (
    <span className={`${A.label} text-appleblue select-none`}>
      {t("directory.common.showing", { from: fromRow, to: toRow, total: totalFiltered })}
    </span>
  );

  return (
    <div className={`w-full ${A.body}`}>
      <ListToolbar
        maxWidthClass="max-w-6xl"
        search={search}
        onSearchChange={setSearch}
        sortValue={sortValue}
        sortOptions={sortOptions}
        onSortChange={setSortValue}
        fromDate={fromDate}
        toDate={toDate}
        onFromDateChange={setFromDate}
        onToDateChange={setToDate}
        newItems={newItems}
        moreItems={moreItems}
        searchPlaceholder={t("spendingPrepaids.search")}
        moreLabel={t("directory.common.more")}
        sortLabel={t("directory.common.sort")}
        fromLabel={t("sales.shared.from")}
        toLabel={t("sales.shared.to")}
        openCalendarLabel={t("sales.shared.openCalendar")}
      />

      {loading ? (
        <div className={`text-center py-6 ${A.body} text-gray-500`}>
          {t("spendingPrepaids.loading")}
        </div>
      ) : err ? (
        <EmptyState
          message={"message" in err ? err.message : t("spendingPrepaids.loadError")}
          heading={t("directory.common.nothingHere")}
          backLabel={t("directory.common.goBack")}
        />
      ) : filtered.length === 0 ? (
        <EmptyState
          message={t("spendingPrepaids.empty")}
          heading={t("directory.common.nothingHere")}
          backLabel={t("directory.common.goBack")}
          showNewButton
          newButtonText={t("spendingPrepaids.newPlus")}
          newButtonPath={companyRoute(company_id, "/prepaids/new")}
        />
      ) : (
        <>
          {/* Mobile Card View */}
          <div className="block md:hidden space-y-3 w-full max-w-6xl px-2 mt-3">
            {paged.map((p) => {
              const ds = displayStatusFor(p);
              return (
                <div
                  key={p.id}
                  onClick={() => openPrepaid(p.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") openPrepaid(p.id);
                  }}
                  className={[
                    A.card,
                    "p-4 transition cursor-pointer",
                    "hover:bg-gray-50 active:ring-2 active:ring-appleblue/20",
                  ].join(" ")}
                  style={{
                    minHeight: 120,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                >
                  <div className="flex justify-between items-center mb-1">
                    <span className={`${A.title} tabular-nums`}>
                      {formatDate(p.date)}
                    </span>
                    <span className={pillClass(statusTone(ds))}>
                      {displayStatusLabel(ds)}
                    </span>
                  </div>

                  <div className="flex justify-between items-center gap-3">
                    <div className="min-w-0">
                      <div className="font-medium text-gray-700 truncate">
                        {p.vendor_name || (
                          <span className="text-gray-400">—</span>
                        )}
                      </div>
                    </div>

                    <div className="text-right">
                      <div className={A.label}>{t("spendingDeposits.remaining")}</div>
                      <div className={`${A.title} ${A.num}`}>
                        {money(p.remaining_amount || 0)}
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-between items-center mt-2 gap-2">
                    <span className={`${A.label} text-gray-500`}>
                      {t("spendingDeposits.original")}:{" "}
                      <span className={A.num}>
                        {money(p.original_amount || 0)}
                      </span>
                    </span>
                    <div className="flex items-center gap-2">
                      <span className={`${A.label} text-gray-400`}>
                        #{p.id}
                      </span>
                      <span className="text-gray-300">›</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination (mobile) */}
          <div className="md:hidden flex justify-between items-center mt-4 w-full max-w-6xl px-2">
            {pagerText}
            <div className="flex gap-2">
              <Button
                variant="tertiary"
                minWidth="0px"
                className="px-2 active:scale-100"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                type="button"
              >
                {t("directory.common.prev")}
              </Button>
              <Button
                variant="tertiary"
                minWidth="0px"
                className="px-2 active:scale-100"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                type="button"
              >
                {t("directory.common.next")}
              </Button>
            </div>
          </div>

          {/* Desktop Table */}
          <div
            className={`${A.card} hidden md:block w-full max-w-6xl mx-auto mt-3 overflow-hidden`}
          >
            <div className="overflow-x-auto">
              <table className={`min-w-full ${A.body}`}>
                <thead>
                  <tr className="border-b border-gray-200">
                    <th
                      className="px-4 py-3 text-left font-medium select-none cursor-pointer"
                      onClick={() =>
                        setSortValue((prev) => {
                          const prevKey = prev.replace(/_(asc|desc)$/, "");
                          const prevDir = prev.endsWith("_asc")
                            ? "asc"
                            : "desc";
                          if (prevKey === "date")
                            return `date_${prevDir === "asc" ? "desc" : "asc"}`;
                          return "date_desc";
                        })
                      }
                      title={t("sales.list.sortDate")}
                    >
                      {t("sales.common.date")}{" "}
                      {sortValue.startsWith("date_") && (
                        <span className="text-gray-400">
                          {sortValue.endsWith("_asc") ? "↑" : "↓"}
                        </span>
                      )}
                    </th>

                    <th
                      className="px-4 py-3 text-left font-medium select-none cursor-pointer"
                      onClick={() =>
                        setSortValue((prev) => {
                          const prevKey = prev.replace(/_(asc|desc)$/, "");
                          const prevDir = prev.endsWith("_asc")
                            ? "asc"
                            : "desc";
                          if (prevKey === "vendor")
                            return `vendor_${prevDir === "asc" ? "desc" : "asc"}`;
                          return "vendor_desc";
                        })
                      }
                      title={t("directory.vendors.sortVendor")}
                    >
                      {t("directory.vendors.singular")}{" "}
                      {sortValue.startsWith("vendor_") && (
                        <span className="text-gray-400">
                          {sortValue.endsWith("_asc") ? "↑" : "↓"}
                        </span>
                      )}
                    </th>

                    <th className="px-4 py-3 text-right font-medium">
                      {t("spendingDeposits.original")}
                    </th>

                    <th
                      className="px-4 py-3 text-right font-medium select-none cursor-pointer"
                      onClick={() =>
                        setSortValue((prev) => {
                          const prevKey = prev.replace(/_(asc|desc)$/, "");
                          const prevDir = prev.endsWith("_asc")
                            ? "asc"
                            : "desc";
                          if (prevKey === "remaining")
                            return `remaining_${prevDir === "asc" ? "desc" : "asc"}`;
                          return "remaining_desc";
                        })
                      }
                      title={t("spendingDeposits.sortRemaining")}
                    >
                      {t("spendingDeposits.remaining")}{" "}
                      {sortValue.startsWith("remaining_") && (
                        <span className="text-gray-400">
                          {sortValue.endsWith("_asc") ? "↑" : "↓"}
                        </span>
                      )}
                    </th>

                    <th className="px-4 py-3 text-left font-medium">{t("sales.common.status")}</th>
                  </tr>
                </thead>

                <tbody>
                  {paged.map((p) => {
                    const ds = displayStatusFor(p);
                    return (
                      <tr
                        key={p.id}
                        onClick={() => openPrepaid(p.id)}
                        className="border-b border-gray-100 bg-white hover:bg-gray-50 cursor-pointer text-gray-700 transition"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") openPrepaid(p.id);
                        }}
                        style={{ outline: "none" }}
                        aria-label={t("expenses.list.viewPrepaid", { id: p.id })}
                      >
                        <td className="px-4 py-3">{formatDate(p.date)}</td>
                        <td className="px-4 py-3 font-medium text-gray-700">
                          {p.vendor_name || (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td
                          className={`px-4 py-3 text-right font-medium text-gray-700 ${A.num}`}
                        >
                          {money(p.original_amount || 0)}
                        </td>
                        <td
                          className={`px-4 py-3 text-right font-medium text-gray-700 ${A.num}`}
                        >
                          {money(p.remaining_amount || 0)}
                        </td>
                        <td className="px-4 py-3">
                          <span className={pillClass(statusTone(ds))}>
                            {displayStatusLabel(ds)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination (desktop) */}
            <div className="flex justify-between items-center mt-2 px-4 pb-4">
              {pagerText}
              <div className="flex gap-2">
                <Button
                  variant="tertiary"
                  minWidth="0px"
                  className="px-2 active:scale-100"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  type="button"
                >
                  {t("directory.common.prev")}
                </Button>
                <Button
                  variant="tertiary"
                  minWidth="0px"
                  className="px-2 active:scale-100"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  type="button"
                >
                  {t("directory.common.next")}
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
